'use client';

import 'keen-slider/keen-slider.min.css';

import cn from 'classnames';
import { useKeenSlider } from 'keen-slider/react';
import { type FC, useRef } from 'react';

import { Text } from '@/components/_rebrand/Text';
import ThemeWrapper from '@/components/_rebrand/ThemeWrapper';
import { ThemeNameType } from '@/tailwind-config/theme.constants';

import Grid from '../Grid';
import type { SocialProofTileProps } from '../SocialProofTile';
import { createAutoplayPlugin } from '../keenSliderPlugins/autoplayPlugin';
import { createWheelControlsPlugin } from '../keenSliderPlugins/wheelControlsPlugin';
import { SLIDER_OPTIONS } from './SocialProof.constants';
import { useOverflowDetection } from './SocialProof.hooks';
import { SocialProofColumn } from './SocialProofColumn';
import { SocialProofSlider } from './SocialProofSlider';

export type SocialProofProps = {
  title: string;
  /**
   * Each entry is a column of 1 or 2 tiles.
   * A column of 1 renders as a single, taller tile (e.g. a StatTile or a QuoteTile).
   * A column of 2 renders as a stacked pair (e.g. two LogoTile instances).
   */
  columns: SocialProofTileProps[][];
  theme?: ThemeNameType;
};

export const SocialProof: FC<SocialProofProps> = ({ title, columns = [], theme }) => {
  const autoplayPlugin = useRef(createAutoplayPlugin()).current;
  const wheelControlsPlugin = useRef(createWheelControlsPlugin()).current;
  const [sliderRef] = useKeenSlider(SLIDER_OPTIONS, [autoplayPlugin, wheelControlsPlugin]);

  const { containerRef, rowOfTilesRef, isOverflowing } = useOverflowDetection(columns);

  return (
    <ThemeWrapper theme={theme || 'theme-gray'}>
      <div className="surface-l0 flex flex-col items-center gap-xl md:gap-2xl py-2xl overflow-hidden">
        <Grid>
          <Text type="h3" as="h3" className="col-span-4 md:col-span-6 md:col-start-4 text-primary text-center text-pretty">
            {title}
          </Text>
        </Grid>

        <div ref={containerRef} className="relative w-full">
          {/* Hidden copy of the static row used only to measure its natural width */}
          <div ref={rowOfTilesRef} className="invisible absolute left-0 top-0 flex items-center gap-xs pointer-events-none">
            {columns.map((column, columnIndex) =>
              <SocialProofColumn key={`${column.map(({ logo }) => logo).join('-')}-${columnIndex}`} column={column} />
            )}
          </div>

          {isOverflowing ? (
            <>
              <div className={cn(
                'absolute -inset-y-[12px] left-0 w-10 sm:w-16 surface-l0 pointer-events-none z-10',
                '[mask-image:linear-gradient(to_right,black,transparent)]'
              )} />

              <SocialProofSlider columns={columns} ref={sliderRef} />

              <div className={cn(
                'absolute -inset-y-[12px] right-0 w-10 sm:w-16 surface-l0 pointer-events-none z-10',
                '[mask-image:linear-gradient(to_left,black,transparent)]'
              )} />
            </>
          ) : (
            <div className="flex items-center justify-center gap-xs">
              {columns.map((column, columnIndex) =>
                <SocialProofColumn key={`${column.map(({ logo }) => logo).join('-')}-${columnIndex}`} column={column} />
              )}
            </div>
          )}
        </div>
      </div>
    </ThemeWrapper>
  );
};
